import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';

import logo from '../assets/vy-life-logo.png?no-inline';
import { ACTIONS, APP_NAME } from '../copy';
import { Form, FormError, SubmitButton, TextField } from '../components';
import {
  areaForPrincipal,
  isRejectedCredential,
  signInWithPassword,
  useSessionState,
} from '../session';
import { AREA_HOME, destinationAfterSignIn } from '../routes/paths';
import { intendedDestination } from '../routes/loginRedirect';

const REJECTED_CREDENTIAL_MESSAGE = 'E-mail ou senha incorretos.';

const END_REASON_MESSAGE = {
  expired: 'Sua sessão terminou. Entre novamente para continuar.',
  signed_out: 'Você saiu da sua conta.',
} as const;

/**
 * The way in.
 *
 * Sign-in failure says exactly one thing, for every way of being wrong. The API
 * answers an unknown address and a wrong password identically so that it cannot
 * be used to find out which addresses are registered, and a UI that said "esse
 * e-mail não existe" would give away what the API was careful not to.
 *
 * Where someone lands afterwards is decided by the server's answer about who
 * they are, not by anything the browser chose -- see
 * {@link destinationAfterSignIn}.
 */
export function LoginPage() {
  const { session, endedReason } = useSessionState();
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const intended = intendedDestination(location.state);

  // Already signed in -- someone reached /entrar with a live session, most
  // likely by pressing Back. Send them where they belong rather than offering
  // to sign in again.
  const area = session === null ? null : areaForPrincipal(session.principal);

  useEffect(() => {
    document.title = `Entrar · ${APP_NAME}`;
  }, []);

  if (session !== null && area !== null) {
    return <Navigate to={destinationAfterSignIn(area, intended)} replace />;
  }

  const handleSubmit = () => {
    setPending(true);
    setError(null);
    void signInWithPassword(email.trim(), password)
      .then((newSession) => {
        const newArea = areaForPrincipal(newSession.principal);
        if (newArea === null) {
          // A machine credential has no web session. The guard explains it.
          void navigate(AREA_HOME.customer, { replace: true });
          return;
        }
        void navigate(destinationAfterSignIn(newArea, intended), { replace: true });
      })
      .catch((cause: unknown) => {
        setError(cause);
        setPassword('');
      })
      .finally(() => {
        setPending(false);
      });
  };

  const rejected = isRejectedCredential(error);

  return (
    <div className="page page--centred">
      <div className="login">
        <h1 className="login__title">
          <img className="login__logo" src={logo} alt={APP_NAME} width={120} height={40} />
        </h1>
        <p className="login__subtitle">Entre com sua conta para continuar.</p>

        {endedReason !== null && error === null && (
          <p className="login__notice" role="status">
            {END_REASON_MESSAGE[endedReason]}
          </p>
        )}

        {rejected ? (
          <div className="form__error" role="alert">
            <p>{REJECTED_CREDENTIAL_MESSAGE}</p>
          </div>
        ) : (
          <FormError error={error} />
        )}

        <Form onSubmit={handleSubmit} aria-label="Entrar">
          <TextField
            label="E-mail"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="username"
            required
            disabled={pending}
          />
          <TextField
            label="Senha"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            required
            disabled={pending}
          />
          <div className="form__actions">
            <SubmitButton pending={pending} pendingLabel="Entrando…">
              {ACTIONS.signIn}
            </SubmitButton>
          </div>
        </Form>

        <p className="login__footnote">
          Por segurança, sua sessão termina quando você fecha o navegador. Recarregar a página
          mantém você conectado; nada seu fica guardado neste computador.
        </p>
      </div>
    </div>
  );
}
